/*
 * Copyright (c) 2008-2019 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */
import { log } from "~/common/src/utils/log"
import api from "@/config/api"
import Vue from "vue"

// Helper method for adding a file to an array in store
const add = key => (state, file) => {
  let oldFile = state[key].find(f => file.fileName == f.fileName)
  if (oldFile) {
    log.info("File already exists updating internal information.")
    oldFile.size = file.size
    oldFile.contentType = file.contentType
  } else {
    state[key].push(file)
  }
}

export default {
  namespaced: true,

  state: {
    features: {},
    featureNames: [],
    votingImages: [],
    interactionImages: [],
    speakerImages: [],
    lockScreenImages: [],
    genericImages: [],
    status: null,
  },

  mutations: {
    /**
     * commit features to state
     *
     * @param state
     * @param features
     */
    loadFeatures(state, features) {
      // store feature objects
      features.forEach(f => {
        Vue.set(state.features, f["name"], f)
      })

      // store meeting IDs
      state.featureNames = features.map(f => f["name"])

      // update status
      state.status = "success"
    },

    loadVotingImages(state, files) {
      log.info(files ? files.length : "0", "voting images loaded")
      Vue.set(state, "votingImages", files)
    },

    deleteVotingImage(state, fileName) {
      let index = state.votingImages.findIndex(i => i.fileName === fileName)

      if (index < 0) {
        log.debug("Voting image with name ", fileName, " was probably already deleted")
        return
      }

      log.info("Deleting voting image", fileName, "at index", index)
      state.votingImages.splice(index, 1)
    },

    addVotingImage(state, file) {
      log.info("Adding uploaded voting image: ", JSON.stringify(file))
      add("votingImages")(state, file)
    },

    loadInteractionImages(state, files) {
      log.info(files ? files.length : "0", "interaction images loaded")
      Vue.set(state, "interactionImages", files)
    },

    deleteInteractionImage(state, fileName) {
      let index = state.interactionImages.findIndex(i => i.fileName === fileName)

      if (index < 0) {
        log.debug("Interaction image with name ", fileName, " was probably already deleted")
        return
      }

      log.info("Deleting interaction image", fileName, "at index", index)
      state.interactionImages.splice(index, 1)
    },

    addInteractionImage(state, file) {
      log.info("Adding uploaded interaction image: ", JSON.stringify(file))
      add("interactionImages")(state, file)
    },

    loadSpeakerImages(state, files) {
      log.info(files ? files.length : "0", "speaker images loaded")
      state.speakerImages = files
    },

    deleteSpeakerImage(state, fileName) {
      let index = state.speakerImages.findIndex(i => i.fileName === fileName)

      if (index < 0) {
        log.debug("Speaker image with name ", fileName, " was probably already deleted")
        return
      }

      log.info("Deleting speaker image", fileName, "at index", index)
      state.speakerImages.splice(index, 1)
    },

    addSpeakerImage(state, file) {
      log.info("Adding uploaded speaker image: ", JSON.stringify(file))
      add("speakerImages")(state, file)
    },

    loadLockScreenImages(state, files) {
      log.info(files ? files.length : "0", "lock screen images loaded")
      state.lockScreenImages = files
    },

    deleteLockScreenImage(state, fileName) {
      log.info("Deleting lock screen image: ", fileName)

      let index = state.lockScreenImages.findIndex(i => i.fileName === fileName)

      if (index < 0) {
        log.debug("Lock screen image with name ", fileName, " was probably already deleted")
        return
      }

      log.info("Deleting lock screen image", fileName, "at index", index)
      state.lockScreenImages.splice(index, 1)
    },

    addLockScreenImage(state, file) {
      log.info("Adding uploaded lock screen image: ", JSON.stringify(file))
      add("lockScreenImages")(state, file)
    },

    addGenericImage(state, file) {
      log.info("Adding uploaded generic image: ", JSON.stringify(file))
      add("genericImages")(state, file)
    },

    loadGenericImages(state, files) {
      log.info(files ? files.length : "0", "generic images loaded")
      state.genericImages = files
    },

    deleteGenericImage(state, fileName) {
      log.info("Deleting generic image: ", fileName)

      let index = state.genericImages.findIndex(i => i.fileName === fileName)

      if (index < 0) {
        log.debug("Generic image with name ", fileName, " was probably already deleted")
        return
      }

      log.info("Deleting generic image", fileName, "at index", index)
      state.genericImages.splice(index, 1)
    },

    /**
     * commit load error to state
     */
    loadError(state) {
      state.status = "error"
    },

    /**
     * Mark CMS data as dirty (unpublished changes on server)
     *
     * @param state
     * @param feature {String}
     */
    markAsUnpublished(state, feature) {
      let f = state.features[feature]
      if (f) {
        log.debug("Marking feature as unpublished:", feature)
        Vue.set(f, "unpublished", true)
      } // else: feature not available
    },

    publish(state) {
      Object.keys(state.features).forEach(f => {
        Vue.set(state.features[f], "unpublished", false)
      })
    },
  },

  actions: {
    /**
     * load CMS features from server
     */
    loadFeatures({ commit }) {
      Vue.http.get(api.CMS_FEATURES()).then(
        response => {
          // todo check, if call was successful and a valid JSON has been returned (or s.th. that can be iterated)
          //    it would be good to handle the case when a string (i.e. error message) has been returned
          commit("loadFeatures", response.body)
        },
        () => {
          commit("loadError")
        }
      )
    },

    loadVotingImages({ commit }) {
      Vue.http.get(api.VOTING_IMAGE_UPLOAD()).then(
        response => {
          log.info("---- response.body: " + JSON.stringify(response.body))
          commit("loadVotingImages", response.body)
        },
        () => {
          commit("loadError")
        }
      )
    },

    deleteVotingImage({ commit }, { fileName }) {
      Vue.http.delete(api.VOTING_IMAGE_DELETE(fileName)).then(
        () => {
          commit("deleteVotingImage", fileName)
        },
        () => {
          commit("loadError")
        }
      )
    },

    loadInteractionImages({ commit }) {
      Vue.http.get(api.INTERACTION_IMAGE_UPLOAD()).then(
        response => {
          log.info("---- response.body: " + JSON.stringify(response.body))
          commit("loadInteractionImages", response.body)
        },
        () => {
          commit("loadError")
        }
      )
    },

    deleteInteractionImage({ commit }, { fileName }) {
      Vue.http.delete(api.INTERACTION_IMAGE_DELETE(fileName)).then(
        () => {
          commit("deleteInteractionImage", fileName)
        },
        () => {
          commit("loadError")
        }
      )
    },

    loadSpeakerImages({ commit }) {
      Vue.http.get(api.CMS_SPEAKER_IMAGES_UPLOAD()).then(
        response => {
          commit("loadSpeakerImages", response.body)
        },
        () => {
          commit("loadError")
        }
      )
    },

    deleteSpeakerImage({ commit }, { fileName }) {
      Vue.http.delete(api.CMS_SPEAKER_IMAGES_DELETE(fileName)).then(
        () => {
          commit("deleteSpeakerImage", fileName)
        },
        () => {
          commit("loadError")
        }
      )
    },

    loadLockScreenImages({ commit }) {
      Vue.http.get(api.CMS_LOCKSCREEN_UPLOAD()).then(
        response => {
          commit("loadLockScreenImages", response.body)
        },
        () => {
          commit("loadError")
        }
      )
    },

    deleteLockScreenImage({ commit }, { fileName }) {
      Vue.http.delete(api.CMS_LOCKSCREEN_DELETE(fileName)).then(
        () => {
          commit("deleteLockScreenImage", fileName)
        },
        () => {
          commit("loadError")
        }
      )
    },

    loadGenericImages({ commit }) {
      Vue.http.get(api.CMS_GENERIC_IMAGES_UPLOAD()).then(
        response => {
          commit("loadGenericImages", response.body)
        },
        () => {
          commit("loadError")
        }
      )
    },

    deleteGenericImage({ commit }, { fileName }) {
      Vue.http.delete(api.CMS_GENERIC_IMAGES_DELETE(fileName)).then(
        () => {
          commit("deleteGenericImage", fileName)
        },
        () => {
          commit("loadError")
        }
      )
    },

    publish({ commit }) {
      log.debug("Publishing updated event data")

      return new Promise((resolve, reject) => {
        Vue.http.put(api.CMS_PUBLISH()).then(
          response => {
            commit("publish")
            resolve(response.data)
          },
          response => {
            log.error("Could not publish data, error: ", response)
            reject(response)
          }
        )
      })
    },
  },

  getters: {
    /**
     * Get whether unpublished status of cms features have successfully loaded
     *
     * @returns {boolean}
     */
    hasLoaded: state => state.status === "success",

    isAnyUnpublished: state => {
      let featureNames = Object.keys(state.features)
      let unpublished = featureNames.filter(f => state.features[f] && state.features[f].unpublished)
      return unpublished.length > 0
    },

    featureNames: state => state.featureNames,

    isUnpublished: state => feature => (state.features[feature] ? state.features[feature].unpublished : false),

    votingImages: state => state.votingImages,

    interactionImages: state => state.interactionImages,

    speakerImages: state => state.speakerImages,

    lockScreenImages: state => state.lockScreenImages,

    genericImages: state => state.genericImages,

    agendaAvailable: state => state.features["agenda"],

    speakerInfoAvailable: state => state.features["speaker-infos"] || state.features["speaker-images"],

    travelInfoAvailable: state => state.features["travel-infos"],

    keyVisualsAvailable: state => state.features["lockscreen-images"],

    genericImagesAvailable: state => state.features["generic-images"],
  },
}
